import React, { ReactNode, useCallback, useMemo } from 'react';

import Autocomplete from '@mui/material/Autocomplete';
import TextField, { TextFieldProps } from '@mui/material/TextField';
import classNames from 'classnames';

import { Box, BoxColors } from '../../../../Box';
import { Badge, OptionBadge, Preloader } from '../../../common';
import { Icon } from '../../../../Icon';
import { Group } from '../common';
import useStyles from '../../SelectInput.styles';
import { ColorIndicator, SelectInputItem } from '../../types';
import { PopupIcon } from '../../PopupIcon';

import { Option } from './Option';
import { COLORS } from './constants';

interface SingleAutocompleteProps<T extends string | number> {
  items: SelectInputItem<T>[];
  label?: string;
  value: T | undefined;
  onChange: (value: T | undefined) => void;
  testId?: string;
  disabled?: boolean;
  placeholder?: string;
  required?: boolean;
  clearable?: boolean;
  loading?: boolean;
  error?: boolean;
  helperText?: string;
  renderOption?: (option: SelectInputItem<T>) => ReactNode;
  emptyOption?: React.ReactNode;
  inputValue: string;
  setInputValue: (value: string) => void;
  isThereSomeGroup: boolean;
  inputId: string;
  handlePopoverOpen: () => void;
  valueBadge: OptionBadge | undefined;
  valueColorIndicator: ColorIndicator;
  onFocus: () => void;
}

export const SingleAutocomplete = <T extends string | number>({
  label,
  value,
  items,
  onChange,
  disabled = false,
  placeholder,
  testId,
  clearable = false,
  loading = false,
  error = false,
  helperText,
  renderOption,
  emptyOption,
  inputValue,
  setInputValue,
  isThereSomeGroup,
  inputId,
  handlePopoverOpen,
  valueBadge,
  valueColorIndicator,
  onFocus,
  ...rest
}: SingleAutocompleteProps<T>) => {
  const { classes } = useStyles();

  const inputClasses = useMemo(
    () => ({
      paper: classNames(classes.list, classes.autocompleteList, {
        [classes.longList]: !!renderOption,
      }),
    }),
    [classes, renderOption],
  );

  const loadingInputProps = useMemo(() => {
    return loading
      ? {
          slotProps: {
            input: {
              endAdornment: <Preloader />,
            },
          },
        }
      : {};
  }, [loading]);

  const inputValueBadgeProps = useMemo(() => {
    return inputValue && valueBadge && !loading
      ? {
          slotProps: {
            input: {
              startAdornment: (
                <Badge
                  title={valueBadge.title}
                  description={valueBadge.description}
                  color={valueBadge.color}
                />
              ),
            },
          },
        }
      : {};
  }, [inputValue, valueBadge, loading]);

  const groupBy = useCallback(({ group }: SelectInputItem<T>) => {
    return group || 'Заявки без группировки по коллекциям';
  }, []);

  const groupProps = useMemo(() => {
    return isThereSomeGroup
      ? {
          groupBy,
        }
      : {};
  }, [isThereSomeGroup, groupBy]);

  const getInputColorIndicator = (
    params: React.JSX.IntrinsicAttributes & TextFieldProps,
  ) => {
    return inputValue && valueColorIndicator && !valueBadge && !loading
      ? {
          InputProps: {
            ...params.InputProps,
            startAdornment: (
              <Box
                width={8}
                height={8}
                ml="16"
                bgcolor={COLORS[valueColorIndicator] as BoxColors}
                radius="round"
              />
            ),
          },
        }
      : {};
  };

  return (
    <Autocomplete<SelectInputItem<T>, false, boolean>
      {...groupProps}
      id={inputId}
      blurOnSelect
      data-testid={testId}
      classes={inputClasses}
      value={items.find((option) => option.value === value) || null}
      inputValue={loading ? 'Загрузка...' : inputValue}
      options={items}
      disabled={disabled || loading}
      disableClearable={!clearable}
      popupIcon={<PopupIcon mx="12" />}
      clearIcon={
        <Icon name="dismiss_substract" color="stroke.secondary" size="24" />
      }
      noOptionsText={emptyOption || 'Ничего не найдено'}
      getOptionDisabled={(option) => !!option.disabled}
      renderGroup={(params) => <Group {...params} />}
      getOptionLabel={(option) => option.label}
      onInputChange={(_, nextValue) => {
        setInputValue(nextValue);
      }}
      onChange={(_, nextValue) => {
        onChange(nextValue?.value as T);
      }}
      onFocus={onFocus}
      renderOption={(props, option) => (
        <Option
          inputValue={inputValue}
          renderOption={renderOption}
          optionProps={props}
          option={option}
        />
      )}
      renderInput={(params: React.JSX.IntrinsicAttributes & TextFieldProps) => (
        <TextField
          {...rest}
          {...params}
          {...loadingInputProps}
          {...inputValueBadgeProps}
          {...getInputColorIndicator(params)}
          id={inputId}
          onClick={handlePopoverOpen}
          classes={{ root: `${classes.inputRoot} aqa_select` }}
          variant="outlined"
          placeholder={placeholder || label}
          error={error}
          helperText={helperText}
        />
      )}
    />
  );
};
